import { PropertyFilterOperator } from "@amzn/awsui-collection-hooks";
import { CollectionPreferencesProps, PropertyFilterProps, SpaceBetween } from "@amzn/awsui-components-react";
import { Link, Button } from "@amzn/uxm-aws-ui-components-react/lib/src/polaris/";
import { TableProps } from "@amzn/awsui-components-react/polaris/table";
import jp from "jsonpath";
import { isArray } from "lodash";
import React from "react";
import { ColumnSetting } from "./config-models";
import StatusIndicator, { StatusIndicatorProps } from "@amzn/awsui-components-react/polaris/status-indicator";
import { tableUtilsData } from "data";
import { OrderStatusStamp } from "@amzn/ito-client";
import { buildDateOnly } from "./time-utils";
import { buildRequestTypeBadge } from "common/utils/form-utils";
import { getString } from "common/ui_string_labels/ui-strings-utils";
import { ExpandableList } from "common/component/ExpandableList/ExpandableList";
import CopyText from "common/component/CopyText/CopyText";
import { retrieveStageFromHostname } from "configuration/config";

const getStringPathLinks = "links";
const getStringPath = "common.tableUtils";

const EMPTY_STRING = "";
const DEFAULT_VISIBLE_ITEMS_COUNT = 5;

const stage = retrieveStageFromHostname();

function getValueOrValues(item: any, selector: Array<any> | string) {
    let value: any = null;
    let values: string[] = [];
    if (isArray(selector)) {
        // Multiple values
        value = "";
        values = [];
        for (const eachSelector of selector) {
            const val = jp.value(item, eachSelector as string);
            value += " " + val;
            values.push(val as string);
        }
    } else {
        // Single value
        // Uses jsonpath to get the value
        value = jp.value(item, selector as string);
    }
    return { value, values };
}

export function buildColumnDefinitions(
    columnSettings: ColumnSetting[],
    onlyThese?: readonly string[],
): TableProps.ColumnDefinition<any>[] {
    const columnDefinitions: TableProps.ColumnDefinition<any>[] = [];
    for (const column of columnSettings) {
        const showIt: boolean = checkIfShowIt(column, onlyThese);
        if (!showIt) {
            continue;
        }
        const cellFunction = (item: any): React.ReactNode => {
            let id: any = null;
            let url = "";
            const { value, values } = getValueOrValues(item, column.selector);

            switch (column.type) {
                case "requester_info":
                    return buildRequesterInfo(values);
                case "view_by_order_id":
                    let taxonomyId = "";

                    if (column.linkSetting) {
                        taxonomyId = encodeURIComponent(jp.value(item, column.linkSetting.idSelector as string));
                    }

                    return (
                        <Link
                            external
                            href={getString("hardwareOrderLink", getStringPathLinks, {
                                stage: stage ? "." + stage : "",
                                taxonomyId,
                            })}
                            variant="secondary"
                            touchpoint="table-view-order-by-id"
                        >
                            {value}
                        </Link>
                    );
                case "status":
                    return buildStatusLabel(value);
                case "last_updated_date":
                    if (value) {
                        return buildLastUpdate(value);
                    }
                    break;
                case "request_type":
                    return buildRequestTypeBadge(values);
                case "build-ship-to":
                    return buildShipTo(values);
                case "requested_items":
                    return buildItemsRequested(value);
                case "request_category":
                    return buildRequestCategory(values);
                case "view_in_itham":
                    return buildViewOrderInItHam(value);
                case "is_expedite":
                    return buildIsExpedite(value);
                case "date_only":
                    return buildExpediteDate(value);
                case "requestedBy_info":
                    return buildRequestedByInfo(value);
                case "legacy_order_id":
                    return buildLegacyOrderId(value);
                default:
                    return <span>{value}</span>;
            }
        };

        columnDefinitions.push({
            ...column,
            cell: cellFunction,
            sortingField: column.sortable ? column.id : undefined,
        });
    }
    return columnDefinitions;
}

export function buildFilteringProperties(columnSettings: ColumnSetting[]): PropertyFilterProps.FilteringProperty[] {
    const filters: PropertyFilterProps.FilteringProperty[] = [];
    for (const column of columnSettings) {
        if (!column.operators && !column.defaultOperator) continue;
        filters.push({
            key: column.id,
            propertyLabel: column.header,
            operators: column.operators ? (column.operators as PropertyFilterOperator[]) : undefined,
            defaultOperator: column.defaultOperator ? (column.defaultOperator as PropertyFilterOperator) : undefined,
            groupValuesLabel: column.header,
        });
    }
    return filters;
}

/**
 * Prepares the options for the preferences, to set visible columns
 * @param columnSettings The settings from the json file
 * @returns The array of options for visible content
 */
export function buildVisibleContentOptions(
    columnSettings: ColumnSetting[],
): CollectionPreferencesProps.VisibleContentOption[] {
    const options: CollectionPreferencesProps.VisibleContentOption[] = [];
    for (const column of columnSettings) {
        if (column.visible === "never") continue;

        options.push({
            id: column.id,
            label: column.header,
            editable: column.visible !== "always",
        });
    }
    return options;
}

export function checkIfShowIt(column: ColumnSetting, onlyThese: readonly string[] | undefined): boolean {
    if (column.visible === "never") {
        return false;
    }
    if (column.visible === "always") {
        return true;
    }
    if (!onlyThese) {
        if (column.visible === "no") {
            // Hidden by default
            return false;
        }

        // Visible by default
        return true;
    } else {
        // Check based on selected preferences
        return onlyThese.indexOf(column.id) >= 0;
    }
}

export function buildStatusLabel(status: string | undefined) {
    const orderStatusMapping: { [key: string]: string } = tableUtilsData.table_utils.statusMapping;
    return buildStatusIndicator(status, orderStatusMapping);
}

function buildStatusIndicator(status: string | undefined, mapping: { [key: string]: string }) {
    const formattedStatus = status
        ? (status as string)
              .toLowerCase()
              .replace(/_/g, " ")
              .replace(/\b\w/g, (l) => l.toUpperCase())
        : "Unable To Retrieve Status";
    const statusType = status ? (mapping[status] as StatusIndicatorProps.Type) : undefined;
    return (
        <span className="status">
            <StatusIndicator type={statusType ?? "error"}>{formattedStatus}</StatusIndicator>
        </span>
    );
}

export function buildLastUpdate(value: OrderStatusStamp[]): React.ReactNode {
    const validEntries = value.length != 0 ? value.filter((entry) => entry.on !== undefined) : [];

    if (validEntries.length === 0) {
        return <div></div>;
    }

    const lastUpdateDate = validEntries.reduce((latest, entry) => {
        return entry.on && latest.on && new Date(entry.on) > new Date(latest.on) ? entry : latest;
    }).on;

    return buildDateOnly(lastUpdateDate);
}

export function buildShipTo(values: any[]): React.ReactNode {
    const [building, deliveryTo] = values;

    if (building) {
        return <span>{building}</span>;
    }

    if (deliveryTo && typeof deliveryTo === "object") {
        const {
            addressLineData = [],
            city = EMPTY_STRING,
            citySubdivision1 = EMPTY_STRING,
            country = EMPTY_STRING,
        } = deliveryTo;

        const line = addressLineData[0] ?? EMPTY_STRING;
        const parts = [line, city, citySubdivision1, country].filter(Boolean);

        return <span>{parts.join(", ")}</span>;
    }

    return <span>-</span>;
}

export function buildRequestCategory(values: any[]): React.ReactNode {
    const [requestCategories, amazonianSubCategory] = values;

    if (Array.isArray(requestCategories) && requestCategories.length > 0) {
        return requestCategories.join(", ");
    }

    if (amazonianSubCategory) {
        return <span>{amazonianSubCategory}</span>;
    }

    return <span>-</span>;
}

export const renderItem = (item: any) => (
    <span key={item.itemId}>
        <strong>{item.quantity}</strong> {item.itemInformation.name}
    </span>
);

export function buildRequestedByInfo(value: string | undefined): React.ReactNode {
    const userAlias = value ?? "alias unavailable";

    return (
        <Link
            external
            href={getString("requesterInfoLink", getStringPathLinks, {
                username: value ?? "",
            })}
            target={"_blank"}
            touchpoint="table-requested-by-info"
        >
            @{userAlias}
        </Link>
    );
}

export function buildLegacyOrderId(value: string | undefined): React.ReactNode {
    const legacyOrderId = value ?? "-";

    return legacyOrderId;
}

export function buildRequesterInfo(values: string[]): React.ReactNode {
    const userAlias = values[0] ?? "alias unavailable";
    const userEmail = values[1] ?? "email unavailable";

    return (
        <SpaceBetween size="xs">
            <Link
                external
                href={getString("requesterInfoLink", getStringPathLinks, {
                    username: userAlias,
                })}
                target={"_blank"}
                touchpoint="table-requester-info"
            >
                @{userAlias}
            </Link>
            <CopyText textContent={userEmail} copyContent={userEmail} isHtml={false} />
        </SpaceBetween>
    );
}

export function buildItemsRequested(value: any[] | undefined) {
    const emptyState = (
        <div style={{ display: "flex", justifyContent: "center" }}>
            <span>-</span>
        </div>
    );

    const getStringItemsRequested = `${getStringPath}.manageOrders.table.expandableView`;

    return (
        <ExpandableList
            data={value || []}
            emptyState={emptyState}
            maxVisible={DEFAULT_VISIBLE_ITEMS_COUNT}
            showLessText={getString(`${getStringItemsRequested}.showLess`)}
            showMoreText={getString(`${getStringItemsRequested}.showMore`)}
            renderItem={renderItem}
        />
    );
}

export function buildIsExpedite(value: boolean) {
    const getStringExpedite = `${getStringPath}.buildIsExpedite`;
    if (value) {
        return <StatusIndicator type="success">{getString(`${getStringExpedite}.isExpedite`)}</StatusIndicator>;
    }

    return (
        <StatusIndicator colorOverride="grey" type="error">
            {getString(`${getStringExpedite}.isNotExpedite`)}
        </StatusIndicator>
    );
}

export function buildViewOrderInItHam(value: string) {
    const currentStage = retrieveStageFromHostname();
    return (
        <div style={{ display: "flex", justifyContent: "center" }}>
            <Button
                href={getString("hardwareOrderLink", getStringPathLinks, {
                    stage: currentStage ? "." + currentStage : "",
                    taxonomyId: encodeURIComponent(value),
                })}
                iconAlign="right"
                iconName="external"
                target="_blank"
                touchpoint="table-view-order-in-itham"
            >
                {getString(`${getStringPath}.buildViewOrderInItHam.viewOrder`)}
            </Button>
        </div>
    );
}

export function buildExpediteDate(value: any) {
    if (value) {
        return buildDateOnly(value);
    }

    return <div>{getString(`${getStringPath}.buildExpediteDate.NonApplicable`)}</div>;
}
